import { useCallback, useEffect, useMemo, useState } from 'react';

import { useIntersectionObserver } from '@common/hooks';
import { Badge } from '@common/ui/Badge';
import { Button } from '@common/ui/buttons/Button';
import { Card } from '@common/ui/Card';
import { Cell } from '@common/ui/Cell';
import { Loader } from '@common/ui/Loader';
import { Separator } from '@common/ui/Separator';
import { Heading } from '@common/ui/typography/Heading';

import { useGetPaymentsHistory } from '@services/hooks/PaymentsService';
import {
  getPaymentsHistory,
  type GetPaymentsHistoryResponse,
} from '@services/PaymentsService';

import styles from './PaymentHistory.module.scss';
import {
  formatAmount,
  formatDate,
  formatProductCode,
  getPaymentStatusView,
} from '../../utils';

export const PaymentHistory = () => {
  const { data: firstPage, isLoading } = useGetPaymentsHistory();

  const [extraPages, setExtraPages] = useState<GetPaymentsHistoryResponse[]>(
    [],
  );
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasLoadMoreError, setHasLoadMoreError] = useState(false);

  // SWR revalidates the first page (e.g. on window focus); the extra pages
  // were fetched relative to the OLD first-page boundary, so keeping them
  // after a shifted first page could silently hide rows. Drop the tail and
  // let the sentinel re-page from the fresh boundary.
  useEffect(() => {
    setExtraPages([]);
    setHasLoadMoreError(false);
  }, [firstPage]);

  const pages = useMemo(
    () => (firstPage ? [firstPage, ...extraPages] : []),
    [firstPage, extraPages],
  );
  const payments = useMemo(
    () => pages.flatMap((page) => page.payments),
    [pages],
  );

  const lastPagination = pages.length
    ? pages[pages.length - 1].pagination
    : null;
  const nextCursor =
    lastPagination?.hasMore && lastPagination.nextCursor
      ? lastPagination.nextCursor
      : null;

  const loadMore = useCallback(async () => {
    if (!nextCursor || isLoadingMore) {
      return;
    }

    setIsLoadingMore(true);
    setHasLoadMoreError(false);
    try {
      const nextPage = await getPaymentsHistory({ before: nextCursor });
      setExtraPages((pages) => [...pages, nextPage]);
    } catch (error) {
      console.error(error);
      // Keep the sentinel disabled until an explicit retry — re-enabling it
      // with the sentinel still visible would re-fire the observer and hammer
      // the endpoint in a loop for as long as the request keeps failing.
      setHasLoadMoreError(true);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, nextCursor]);

  const sentinelRef = useIntersectionObserver({
    rootMargin: '100px',
    threshold: 0.1,
    enabled:
      Boolean(nextCursor) && !isLoadingMore && !isLoading && !hasLoadMoreError,
    onIntersect: () => {
      void loadMore();
    },
  });

  return (
    <Card testId="payments-history" className={styles.paymentHistory}>
      <Heading level={3} style={{ padding: 0 }}>
        История платежей
      </Heading>

      {isLoading && <Loader className={styles.loader} />}

      {!isLoading && payments.length === 0 && (
        <div className={styles.empty} data-test-id="payments-history-empty">
          Платежей пока нет
        </div>
      )}

      {payments.map((payment, index) => (
        <Cell
          key={payment.id}
          testId={`payments-history-item-${payment.id}`}
          title={formatProductCode(payment.productCode)}
          subtitle={formatDate(payment.paidAt) || '—'}
          suffixIcon={
            <div className={styles.amountColumn}>
              <span className={styles.amount}>
                {formatAmount(payment.amount, payment.currency)}
              </span>
              <Badge
                testId={`payments-history-status-${payment.id}`}
                color={getPaymentStatusView(payment.status).color}
                text={getPaymentStatusView(payment.status).text}
              />
            </div>
          }
          separator={index < payments.length - 1 ? <Separator /> : undefined}
        />
      ))}

      {nextCursor && (
        <div ref={sentinelRef} style={{ height: 1, flexShrink: 0 }} />
      )}

      {isLoadingMore && <Loader className={styles.loader} />}

      {hasLoadMoreError && !isLoadingMore && (
        <Button
          testId="payments-history-retry"
          variant="neutralSecondary"
          onClick={() => {
            void loadMore();
          }}
        >
          Не удалось загрузить. Повторить
        </Button>
      )}
    </Card>
  );
};
